import config from './config';
import { DomainBrand } from './branding-api';

/**
 * FS10: Utility functions for constructing checkpoint URLs
 * Centralizes URL construction logic for short URLs and QR codes
 */

/**
 * Determine the protocol for a domain
 * Always uses HTTP for localhost (regardless of sslEnabled flag)
 */
export const getProtocol = (domain: string, sslEnabled?: boolean): string => {
  // Never use HTTPS for localhost - it won't work
  if (domain.startsWith('localhost') || domain.startsWith('127.0.0.1')) {
    return 'http://';
  }
  return sslEnabled ? 'https://' : 'http://';
};

/**
 * Construct the short URL for a checkpoint
 * @param shortCode - The short code
 * @param domain - Optional custom domain (e.g., "go.example.com")
 * @param sslEnabled - Whether SSL is enabled for the domain
 */
export const buildShortUrl = (
  shortCode: string,
  domain?: string,
  sslEnabled?: boolean
): string => {
  if (domain) {
    const protocol = getProtocol(domain, sslEnabled);
    return `${protocol}${domain}/${shortCode}`;
  }
  return `${config.apiBaseUrl}/${shortCode}`;
};

/**
 * Construct the QR code URL for a checkpoint
 * @param shortCode - The short code
 * @param domain - Optional custom domain (e.g., "go.example.com")
 * @param sslEnabled - Whether SSL is enabled for the domain
 */
export const buildQrCodeUrl = (
  shortCode: string,
  domain?: string,
  sslEnabled?: boolean
): string => {
  if (domain) {
    const protocol = getProtocol(domain, sslEnabled);
    return `${protocol}${domain}/api/qr/${shortCode}`;
  }
  return `${config.apiBaseUrl}/api/qr/${shortCode}`;
};

export interface ShortenedUrlResponse {
  originalUrl: string;
  shortUrl: string;
  shortCode: string;
  qrCodeUrl?: string;
  expiresAt?: string;
  name?: string;  // FS10: Added for home page checkpoint display
  isActive?: boolean;  // FS10: Added for home page status display
  id?: number;  // FS10: URL mapping ID for API calls
}

export interface AccountBrandingInfo {
  id: number;
  logoFileName: string;
  originalFileName: string;
}

export interface WorkspaceInfo {
  id: number;
  name: string;
  description: string | null;
  tags: string | null;
  accountBrandingId: number | null;
  domainBrandId: number | null;  // Loose reference to BrandService - fetch via getDomainBrands()
  accountBranding: AccountBrandingInfo | null;
}

export interface UrlMapping {
  id: number;
  name: string;  // FS8: Mandatory name for the checkpoint
  description?: string;  // FS4: Optional description for the checkpoint
  originalUrl: string;
  shortCode: string;
  shortUrl: string;
  domain?: string;  // FR-13: Domain field
  sslEnabled?: boolean;  // FR-21: SSL status field (defaults to true)
  createdAt: string;
  expiresAt?: string;
  startsAt?: string;  // FR-33: Start date
  clicks: number;
  // FR-32/FR-33: Track if dates are custom or inherited
  hasCustomExpiresAt?: boolean;
  hasCustomStartsAt?: boolean;
  tags?: string;
  qrCodeUrl: string;
  workspaceId?: number | null;
  workspace?: WorkspaceInfo | null;  // FR-14: Full workspace information
  // FR-22: Activation fields
  isActive?: boolean;
  deactivationSource?: string | null;
  deactivatedAt?: string | null;
  deactivatedBy?: string | null;
}

export interface Workspace {
  id: number;
  name: string;
  description: string | null;
  tags: string | null;
  accountBrandingId: number | null;
  domainBrandId: number | null;
  accountBranding: AccountBranding | null;
  domainBrand: DomainBrand | null;
  createdAt: string;
  updatedAt: string | null;
  // FR-23: Activation fields
  isActive?: boolean;
  deactivatedRedirectUrl?: string | null;
  // FR-24: Geo-targeting fields
  allowedCountries?: string | null;  // JSON string: ["US", "HU"]
  allowedRegions?: string | null;    // JSON string: ["Budapest", "California"]
  geoBlockBehavior?: string;         // "404" or "Redirect"
  // FR-32/FR-33: Workspace date fields
  expiresAt?: string | null;  // ISO 8601 date string
  startsAt?: string | null;   // ISO 8601 date string
}

export interface AccountBranding {
  id: number;
  logoFileName: string;
  originalFileName: string;
}

/**
 * Shorten a URL using the Sqrone API
 * @param url - The URL to shorten
 * @param name - FS8: Mandatory name for the checkpoint
 * @param workspaceId - Optional workspace ID
 * @param token - Optional authentication token
 * @param domain - Optional domain (e.g., "go.example.com")
 * @param customShortCode - Optional custom short code (e.g., "summer-promo")
 * @param sslEnabled - Whether SSL is enabled for the domain (auto-detected by backend if not provided)
 * @returns The shortened URL response
 */
export const shortenUrl = async (
  url: string,
  name: string,  // FS8: Mandatory name
  workspaceId: number | null = null,
  token: string | null = null,
  domain?: string,
  customShortCode?: string,
  sslEnabled?: boolean
): Promise<UrlMapping> => {
  const apiUrl = `${config.apiBaseUrl}/api/shorten`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      url,
      name,  // FS8: Mandatory name
      workspaceId: workspaceId || undefined,
      domain: domain || undefined,
      customShortCode: customShortCode || undefined,
      ...(sslEnabled !== undefined && { sslEnabled }),  // Only include if explicitly set
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to shorten URL');
  }

  return await response.json();
};

/**
 * Get URL details by short code
 * @param shortCode - The short code to look up
 * @param token - Optional authentication token
 * @returns The URL details
 */
export const getUrl = async (
  shortCode: string,
  token: string | null = null
): Promise<ShortenedUrlResponse> => {
  const apiUrl = `${config.apiBaseUrl}/api/url/${shortCode}`;
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to get URL details');
  }

  return await response.json();
};

/**
 * Update a URL by short code
 * @param shortCode - The short code to update
 * @param updateData - The data to update
 * @param token - Authentication token (required)
 * @returns The updated URL details
 */
export const updateUrl = async (
  shortCode: string,
  updateData: Partial<Pick<UrlMapping, 'originalUrl' | 'name' | 'description' | 'tags' | 'expiresAt' | 'startsAt'>>,
  token: string
): Promise<UrlMapping> => {
  const apiUrl = `${config.apiBaseUrl}/api/url/${shortCode}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to update URL');
  }

  return await response.json();
};

/**
 * Update a URL's short code
 * @param id - The URL ID
 * @param newShortCode - The new short code
 * @param token - Authentication token (required)
 * @returns The updated URL details
 */
export const updateShortCode = async (
  id: number,
  newShortCode: string,
  token: string
): Promise<UrlMapping> => {
  const apiUrl = `${config.apiBaseUrl}/api/url/${id}/shortcode`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ shortCode: newShortCode }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to update short code');
  }

  return await response.json();
};

/**
 * Delete a URL by short code
 * @param shortCode - The short code to delete
 * @param token - Authentication token (required)
 * @returns Success message
 */
export const deleteUrl = async (
  shortCode: string,
  token: string
): Promise<{ message: string }> => {
  const apiUrl = `${config.apiBaseUrl}/api/url/${shortCode}`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete URL');
  }

  // Handle empty response (204 No Content) or parse JSON
  const contentType = response.headers.get('content-type');
  if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
    return { message: 'URL deleted successfully' };
  }

  return await response.json();
};

/**
 * Get QR code for a short code
 * @param shortCode - The short code to get QR code for
 * @param token - Optional authentication token
 * @returns QR code data URL
 */
export const getQrCode = async (
  shortCode: string,
  token: string | null = null
): Promise<{ qrCodeDataUrl: string }> => {
  const apiUrl = `${config.apiBaseUrl}/api/qr/${shortCode}`;
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to get QR code');
  }

  return await response.json();
};

/**
 * Get all URLs for the authenticated user
 * @param token - Authentication token (required)
 * @returns Array of user's URLs
 */
export const getMyUrls = async (
  token: string
): Promise<UrlMapping[]> => {
  const apiUrl = `${config.apiBaseUrl}/api/urls/my-urls`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to get user URLs');
  }

  return await response.json();
};

/**
 * Validate if a string is a valid URL
 * @param urlString - The URL string to validate
 * @returns True if valid URL
 */
export const isValidUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};


/**
 * Return location of the logo on the server
 * @param logoFile - GUID and extension of the file
 * @returns Link to download the logo
 */
export const getStoredLogo = (logoFile : string ): string => {
  return `${config.apiBaseUrl}/uploads/logos/${logoFile}`;
}

/**
 * Get all workspaces for the authenticated user
 * @param token - Authentication token (required)
 * @returns Array of user's workspaces
 */
export const getWorkspaces = async (token: string): Promise<Workspace[]> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to get workspaces');
  }

  return await response.json();
};

// FR-24: Geo-restriction request interface
export interface GeoRestrictionsRequest {
  allowedCountries?: string[] | null;
  allowedRegions?: string[] | null;
  geoBlockBehavior?: string;
  deactivatedRedirectUrl?: string | null;
}

/**
 * FR-22: Activate a URL by ID
 * @param id - The URL ID
 * @param token - Authentication token (required)
 * @returns The activated URL details
 */
export const activateUrl = async (
  id: number,
  token: string
): Promise<UrlMapping> => {
  const apiUrl = `${config.apiBaseUrl}/api/url/${id}/activate`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to activate URL');
  }

  return await response.json();
};

/**
 * FR-22: Deactivate a URL by ID
 * @param id - The URL ID
 * @param token - Authentication token (required)
 * @returns The deactivated URL details
 */
export const deactivateUrl = async (
  id: number,
  token: string
): Promise<UrlMapping> => {
  const apiUrl = `${config.apiBaseUrl}/api/url/${id}/deactivate`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to deactivate URL');
  }

  return await response.json();
};

/**
 * FR-23: Activate a workspace by ID (cascades to all URLs)
 * @param id - The workspace ID
 * @param token - Authentication token (required)
 * @returns The activated workspace details
 */
export const activateWorkspace = async (
  id: number,
  token: string
): Promise<Workspace> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces/${id}/activate`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to activate workspace');
  }

  return await response.json();
};

/**
 * FR-23: Deactivate a workspace by ID (cascades to all URLs)
 * @param id - The workspace ID
 * @param token - Authentication token (required)
 * @returns The deactivated workspace details
 */
export const deactivateWorkspace = async (
  id: number,
  token: string
): Promise<Workspace> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces/${id}/deactivate`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to deactivate workspace');
  }

  return await response.json();
};

/**
 * Delete a workspace by ID
 * @param id - The workspace ID
 * @param token - Authentication token (required)
 */
export const deleteWorkspace = async (
  id: number,
  token: string
): Promise<void> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces/${id}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to delete workspace');
  }
};

/**
 * FR-24: Update geo-restrictions for a workspace
 * @param id - The workspace ID
 * @param data - The geo-restriction configuration
 * @param token - Authentication token (required)
 * @returns The updated workspace details
 */
export const updateWorkspaceGeoRestrictions = async (
  id: number,
  data: GeoRestrictionsRequest,
  token: string
): Promise<Workspace> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces/${id}/geo-restrictions`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to update geo-restrictions');
  }

  return await response.json();
};

// FR-32/FR-33: Check for URLs with custom dates before updating workspace dates
export const previewWorkspaceDateUpdate = async (id: number, token: string): Promise<{ urlsWithCustomExpiry: number; urlsWithCustomStart: number; totalUrls: number }> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces/${id}/update-dates`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({ previewOnly: true }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to preview date update');
  }

  return await response.json();
};

// FR-32/FR-33: Update workspace dates and propagate to child URLs
export const updateWorkspaceDates = async (
  id: number,
  expiresAt: string | null,
  startsAt: string | null,
  overrideCustomDates: boolean,
  token: string
): Promise<{ message: string; urlsUpdated: number; workspace: any }> => {
  const apiUrl = `${config.apiBaseUrl}/api/workspaces/${id}/update-dates`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      expiresAt,
      startsAt,
      overrideCustomDates,
      previewOnly: false,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update workspace dates');
  }

  return await response.json();
};

// FR-35: Validate custom short code availability
export const validateShortCode = async (shortCode: string, domain: string, token: string): Promise<{ available: boolean; message: string }> => {
  const apiUrl = `${config.apiBaseUrl}/api/validate-shortcode?shortCode=${encodeURIComponent(shortCode)}&domain=${encodeURIComponent(domain)}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to validate short code');
  }

  return await response.json();
};

// FS10: Claim anonymous checkpoints after user signs in
export const claimUrls = async (
  shortCodes: string[],
  token: string
): Promise<{ claimed: string[]; failed: string[] }> => {
  const apiUrl = `${config.apiBaseUrl}/api/urls/claim`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({ shortCodes }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Failed to claim checkpoints');
  }

  return await response.json();
};

// FS9: Validate checkpoint name - warn if name already exists
export const validateName = async (
  name: string,
  token: string,
  workspaceId?: number | null,
  excludeUrlId?: number | null
): Promise<{ exists: boolean; message: string; existingUrlId?: number; existingShortCode?: string }> => {
  let apiUrl = `${config.apiBaseUrl}/api/validate-name?name=${encodeURIComponent(name)}`;

  if (workspaceId) {
    apiUrl += `&workspaceId=${workspaceId}`;
  }

  if (excludeUrlId) {
    apiUrl += `&excludeUrlId=${excludeUrlId}`;
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to validate name');
  }

  return await response.json();
};